import { ActionsHelperService } from '@/domain/actions/actions.helper.service';
import { UniverseService } from '@/domain/universes/universe.service';
import type { CoreApiPaginatedRequestParams } from '@/rosa';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, type OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AbstractWidgetClass } from '../../../../components/widgets/AbstractWidget.class';
import { AccessLevel } from '../../../../interfaces/Access.interface';
import type { BuildingId } from '../../../../interfaces/Building.interface';
import type { ClassificationNodeId } from '../../../../interfaces/Classification.interface';
import type {
    Subtitle,
    SummaryActionElement,
    SummaryListElement,
    SummaryNoData
} from '../../../../interfaces/SummaryListElement.interface';
import { BuildingsProvider } from '../../../../providers/buildings/buildings.provider';
import { DashboardingProvider } from '../../../../providers/dashboarding.provider';
import { ACTIONS_RIGHT_ACCESS } from '../../../../providers/rights.constants';
import { WidgetSize } from '../../../../providers/widgets.contants';
import { ActionPlanFeatureService } from '../../action-plan-feature.service';
import type { AdvAction, CoreAdvActionRequestBody } from '../../action-plan.interface';
import { ActionPlanService } from '../../services/action-plan.service';
import { SummaryCardComponent } from './summary-card/summary-card';
@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./widget-building-action-summary.scss'],
    selector: 'widget-building-action-summary',
    templateUrl: 'widget-building-action-summary.html',
    imports: [SummaryCardComponent, AsyncPipe]
})
export class WidgetBuildingActionSummaryComponent extends AbstractWidgetClass<null, void> implements OnInit {
    private _actionsService = inject(ActionPlanService);
    private _actionsHelperService = inject(ActionsHelperService);
    private _universeService = inject(UniverseService);
    private _buildingdProvider = inject(BuildingsProvider);
    private _referenceListsService = inject(ReferenceListsService);
    private _translateService = inject(TranslateService);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _actionPlanFeatureService = inject(ActionPlanFeatureService);
    private _router = inject(Router);

    size = WidgetSize.HALF;

    private _buildingId!: BuildingId;
    actionList: SummaryListElement[] = [];
    rightNeeded: string = ACTIONS_RIGHT_ACCESS;

    actionListAction: SummaryActionElement = {
        title: 'BUTTONS.SEE_ACTIONS',
        click: () => {
            this._router.navigate(['/', 'actions']);
        }
    };

    actionNoDataInfo$!: Observable<SummaryNoData>;
    isLoading: boolean = true;
    loadingError: boolean = false;

    ngOnInit() {
        // Widget available only for mono site
        this._dashboardingProvider
            .getMeaningfulBuildingId$()
            .pipe(untilDestroyed(this))
            .subscribe(buildingId => {
                this._buildingId = buildingId;
                this.actionList = [];

                this.fetchActions();

                this.actionNoDataInfo$ = this._buildingdProvider.hasMinimumBuildingAccess$(this._buildingId, AccessLevel.WRITE).pipe(
                    map(hasAccess => {
                        return {
                            icon: 'check',
                            title: 'PAGES.BUILDING_HOME.ACTIONS.NO_ACTION_TITLE',
                            subtitle: 'PAGES.BUILDING_HOME.ACTIONS.NO_ACTION_SUBTITLE',
                            actionBtn: hasAccess
                                ? {
                                      text: 'PAGES.BUILDING_HOME.ACTIONS.CREATE_ACTION',
                                      callback: () => {
                                          this._actionPlanFeatureService.requestActionCreation();
                                      }
                                  }
                                : null!
                        };
                    })
                );
            });
    }

    private getActionSubtitle(action: AdvAction): Subtitle[] {
        const subtitle: Subtitle[] = [];

        // Type
        if (action.type) {
            subtitle.push({ text: this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.ACTION_TYPE, action.type) });
        }

        // ManagedBy
        let textManagedBy = '';
        if (action.managed_by && action.managerInfo) {
            textManagedBy = action.managerInfo.finalNameOrEmail;
            subtitle.push({ text: textManagedBy });
        } else {
            textManagedBy = this._translateService.instant('PAGES.BUILDING_HOME.NO_MANAGED_BY');
            subtitle.push({ text: textManagedBy, color: 'danger' });
        }

        // Date
        let textDate = '';
        if (action.expectedDate) {
            const expectedDate = moment(action.expectedDate).format(this._translateService.instant('DATE_FORMAT.DEFAULT'));
            textDate = this._translateService.instant('PAGES.BUILDING_HOME.ACTION_DATE', { date: expectedDate });

            const actionExpired = moment(action.expectedDate).diff(moment(), 'days');
            if (actionExpired > 0) {
                subtitle.push({ text: textDate, color: 'failure' });
            } else {
                subtitle.push({ text: textDate });
            }
        } else {
            textDate = this._translateService.instant('PAGES.BUILDING_HOME.NO_EXPECTED_DATE');
            subtitle.push({ text: textDate, color: 'danger' });
        }

        return subtitle;
    }

    private fetchActions() {
        const requestParams: CoreApiPaginatedRequestParams = {
            sort: [
                {
                    name: 'status',
                    direction: 'desc'
                },
                {
                    name: 'expectedDate',
                    direction: 'asc'
                }
            ],
            size: 5,
            page: 0
        };

        const bodyParams: CoreAdvActionRequestBody = {
            buildingsFilters: { isDynamicSelectionEnabled: true, classificationNodeId: this._buildingId as ClassificationNodeId },
            statuses: ['todo', 'pending', 'done']
        };

        this.isLoading = true;
        this.loadingError = false;
        this._actionsService
            .fetchActionsFilteredAndPaginatedLegacy(bodyParams, requestParams)
            .then(response => {
                const actions = response.content;
                if (actions) {
                    const notDone = actions.filter(action => action.status !== 'done');
                    let actionsData: any[] = [];
                    if (notDone.length > 0) {
                        actionsData = notDone;
                    } else {
                        actionsData = actions;
                    }
                    this.actionList = actionsData.map(action => ({
                        avatarImageUrl: this._universeService.getUniverseColoredImage$(action.universe),
                        title: action.title,
                        subtitle: this.getActionSubtitle(action),
                        click: () => {
                            this._router.navigate(['/', 'actions', action.action_uid, 'edition']);
                        },
                        chipText: this._actionsHelperService.getReadableStatus(action.status),
                        chipColorName: this._actionsHelperService.getActionStatusColor(action.status)
                    }));
                    this.isLoading = false;
                }
            })
            .catch(error => {
                this.isLoading = false;
                this.loadingError = true;
                throw error;
            });
    }
}
